import type {
  PropertyValueItem,
  WorkflowTaskDetail,
} from "../services/regulationsService";
import {
  buildDynamicFormExtraState,
  propertyValueItemsToDynamicFormConfig,
  type OptionsByField,
  type TaskDetailDynamicFormConfig,
} from "./taskDetailToDynamicFormConfig";

export interface AssignNextTaskDefinition {
  enabled?: boolean;
  label?: string;
  field?: string;
  nextTaskName?: string;
  /** When true, assign-next fields are marked required (red * on label). */
  required?: boolean;
  customPropertyMap?: Record<string, unknown>;
  propertyValueItemList?: PropertyValueItem[];
  fields?: PropertyValueItem[];
  taskId?: string | null;
  taskDefinitionId?: string;
  taskInstanceUniqueId?: string;
  processAssociationId?: string;
  taskCustomAssignee?: {
    taskUsers?: string[];
    taskGroups?: string[];
    taskRoles?: string[];
  };
  [key: string]: unknown;
}

function asPropertyValueItemList(value: unknown): PropertyValueItem[] {
  if (!Array.isArray(value)) return [];
  return value.filter(
    (item): item is PropertyValueItem =>
      Boolean(item) && typeof item === "object" && item.field != null,
  );
}

function collectFieldDefinitionSources(
  taskDetail: WorkflowTaskDetail | null | undefined,
): PropertyValueItem[] {
  const taskList = taskDetail?.task?.propertyValueItemList ?? [];
  const headerList = Array.isArray(taskDetail?.taskPage?.headers)
    ? taskDetail.taskPage.headers
    : [];
  return [...taskList, ...headerList];
}

function resolveAssignNextTaskFieldLabel(
  assignNextTask: AssignNextTaskDefinition,
): string | undefined {
  if (typeof assignNextTask.label !== "string") return undefined;
  const trimmed = assignNextTask.label.trim();
  return trimmed || undefined;
}

/** Prefer assignNextTask.label; apply required; strip description (no info tooltip). */
function withAssignNextTaskFieldPresentation(
  items: PropertyValueItem[],
  assignNextTask: AssignNextTaskDefinition,
): PropertyValueItem[] {
  if (items.length === 0) return items;
  const fieldLabel = resolveAssignNextTaskFieldLabel(assignNextTask);
  const required = assignNextTask.required === true;

  return items.map((item) => {
    const next: PropertyValueItem = { ...item };
    if (fieldLabel) {
      next.label = fieldLabel;
    }
    // Dynamic Form CustomizedFormLabel shows an info Tooltip when description is set.
    next.description = "";
    if (required) {
      next.required = true;
    }
    return next;
  });
}

function resolveAssignNextTaskPropertyItems(
  assignNextTask: AssignNextTaskDefinition,
  taskDetail: WorkflowTaskDetail | null | undefined,
): PropertyValueItem[] {
  const explicit =
    asPropertyValueItemList(assignNextTask.propertyValueItemList).length > 0
      ? asPropertyValueItemList(assignNextTask.propertyValueItemList)
      : asPropertyValueItemList(assignNextTask.fields);
  if (explicit.length > 0) {
    return withAssignNextTaskFieldPresentation(explicit, assignNextTask);
  }

  const customPropertyMap = assignNextTask.customPropertyMap;
  if (!customPropertyMap || typeof customPropertyMap !== "object") {
    return [];
  }

  const definitions = collectFieldDefinitionSources(taskDetail);
  const resolved: PropertyValueItem[] = [];

  for (const fieldName of Object.keys(customPropertyMap)) {
    const definition = definitions.find(
      (item) => item.field != null && String(item.field) === fieldName,
    );
    if (!definition) continue;

    const fieldValue = customPropertyMap[fieldName];
    resolved.push({
      ...definition,
      hide: false,
      ...(fieldValue !== undefined ? { fieldValue } : {}),
    });
  }

  return withAssignNextTaskFieldPresentation(resolved, assignNextTask);
}

export function parseAssignNextTaskFromTaskDetail(
  taskDetail: WorkflowTaskDetail | null | undefined,
): AssignNextTaskDefinition | null {
  const raw = taskDetail?.taskPage?.assignNextTask;
  if (!raw || typeof raw !== "object") return null;

  const assignNextTask = raw as AssignNextTaskDefinition;
  if (assignNextTask.enabled === false) return null;
  return assignNextTask;
}

/** True when assignNextTask.label is a non-empty string (Save should be hidden). */
export function hasAssignNextTaskConfig(
  taskDetail: WorkflowTaskDetail | null | undefined,
): boolean {
  const raw = taskDetail?.taskPage?.assignNextTask;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return false;
  const label = (raw as AssignNextTaskDefinition).label;
  return typeof label === "string" && label.trim().length > 0;
}

export function taskDetailToAssignNextTaskDynamicFormConfig(
  taskDetail: WorkflowTaskDetail | null | undefined,
  optionsByField?: OptionsByField,
): TaskDetailDynamicFormConfig {
  const assignNextTask = parseAssignNextTaskFromTaskDetail(taskDetail);
  if (!assignNextTask) {
    return { jsonData: [], initialFormData: {} };
  }

  const propertyItems = resolveAssignNextTaskPropertyItems(
    assignNextTask,
    taskDetail,
  );
  const config = propertyValueItemsToDynamicFormConfig(
    propertyItems,
    optionsByField,
    "assignNextTask",
  );

  const initialFormData = { ...config.initialFormData };
  const customPropertyMap = assignNextTask.customPropertyMap ?? {};
  for (const [field, value] of Object.entries(customPropertyMap)) {
    if (value !== undefined) {
      initialFormData[field] = value;
    }
  }

  if (assignNextTask.taskCustomAssignee) {
    initialFormData.taskOwners = assignNextTask.taskCustomAssignee;
  }

  return {
    ...config,
    initialFormData,
  };
}

export function buildAssignNextTaskFormExtraState(
  taskDetail: WorkflowTaskDetail | null | undefined,
): Record<string, unknown> {
  const assignNextTask = parseAssignNextTaskFromTaskDetail(taskDetail);
  const base = buildDynamicFormExtraState(taskDetail);
  if (!assignNextTask?.taskCustomAssignee) return base;

  return {
    ...base,
    taskOwners: assignNextTask.taskCustomAssignee,
  };
}

/** True when assign-next select/assignee (or plain text) has no usable value. */
export function isAssignNextTaskValueEmpty(value: unknown): boolean {
  if (value === undefined || value === null) return true;
  if (typeof value === "string") return value.trim() === "";
  if (typeof value === "number" || typeof value === "boolean") return false;
  if (Array.isArray(value)) {
    return (
      value.length === 0 || value.every((entry) => isAssignNextTaskValueEmpty(entry))
    );
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if ("value" in obj || "id" in obj) {
      return isAssignNextTaskValueEmpty(obj.value ?? obj.id);
    }
    return Object.keys(obj).length === 0;
  }
  return false;
}

export function validateAssignNextTaskFormData(
  jsonData: TaskDetailDynamicFormConfig["jsonData"],
  formData: Record<string, unknown>,
): string | null {
  for (const item of jsonData) {
    if (!item.required) continue;
    const field = item.field != null ? String(item.field) : "";
    if (!field) continue;

    if (isAssignNextTaskValueEmpty(formData[field])) {
      const label =
        (typeof item.label === "string" && item.label.trim()) ||
        (typeof item.originlabel === "string" && item.originlabel.trim()) ||
        field;
      return `${label} is required.`;
    }
  }
  return null;
}

/** Merge assign-next field values back into the API payload shape. */
export function buildAssignNextTaskSubmitValue(
  assignNextTask: AssignNextTaskDefinition | null | undefined,
  formData: Record<string, unknown>,
): AssignNextTaskDefinition | undefined {
  if (!assignNextTask) return undefined;

  const customPropertyMap = { ...(assignNextTask.customPropertyMap ?? {}) };
  for (const field of Object.keys(customPropertyMap)) {
    if (formData[field] !== undefined) {
      customPropertyMap[field] =
        field === "taskAssignee"
          ? normalizeAssignNextTaskAssignee(formData[field])
          : formData[field];
    }
  }

  for (const item of asPropertyValueItemList(assignNextTask.propertyValueItemList)) {
    const field = item.field != null ? String(item.field) : "";
    if (!field || field in customPropertyMap) continue;
    if (formData[field] !== undefined) {
      customPropertyMap[field] =
        field === "taskAssignee"
          ? normalizeAssignNextTaskAssignee(formData[field])
          : formData[field];
    }
  }

  return {
    ...assignNextTask,
    customPropertyMap,
  };
}

function normalizeAssignNextTaskAssignee(value: unknown): string | null {
  if (value === null || value === undefined || value === "") return null;
  if (Array.isArray(value)) {
    const first = value.find((entry) => entry != null && entry !== "");
    return first != null ? String(first) : null;
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    if (obj.value != null && obj.value !== "") return String(obj.value);
    if (obj.id != null && obj.id !== "") return String(obj.id);
  }
  return String(value);
}
